import React, { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Sun, Star, Calendar, UserCheck, List, Plus, ChevronDown, ChevronRight,
  MoreHorizontal, Search, Settings, BarChart3, AtSign, FolderPlus, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for tasks state
const TasksContext = createContext(null);
export const useTasksContext = () => useContext(TasksContext);

export default function TasksLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [lists, setLists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showNewListDialog, setShowNewListDialog] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListColor, setNewListColor] = useState('#3B82F6');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const token = localStorage.getItem('token');

  const fetchLists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/task-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLists(data.lists || []);
    } catch (err) {
      console.error('Error fetching lists:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/api/task-list-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/task-reports/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchLists();
    fetchGroups();
    fetchStats();
  }, [refreshTrigger]);

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await fetch(`${API_URL}/api/task-lists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: newListName, color: newListColor })
      });
      setNewListName('');
      setShowNewListDialog(false);
      fetchLists();
    } catch (err) {
      console.error('Error creating list:', err);
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Supprimer cette liste ?')) return;
    try {
      await fetch(`${API_URL}/api/task-lists/${listId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLists();
      if (location.pathname.includes(listId)) {
        navigate('/tasks');
      }
    } catch (err) {
      console.error('Error deleting list:', err);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const smartLists = [
    { id: 'my-day', name: 'Ma journée', icon: Sun, path: '/tasks/my-day', color: '#F59E0B', count: stats.my_day_count },
    { id: 'important', name: 'Important', icon: Star, path: '/tasks/important', color: '#EF4444', count: stats.important_count },
    { id: 'planned', name: 'Planifié', icon: Calendar, path: '/tasks/planned', color: '#10B981', count: stats.by_status?.todo },
    { id: 'assigned', name: 'Assigné à moi', icon: UserCheck, path: '/tasks/assigned', color: '#8B5CF6', count: null },
    { id: 'mentions', name: 'Mentions', icon: AtSign, path: '/tasks/mentions', color: '#EC4899', count: null },
    { id: 'all', name: 'Toutes les tâches', icon: List, path: '/tasks', color: '#6B7280', count: stats.pending }
  ];

  // Group lists by group_id
  const listsWithoutGroup = lists.filter(l => !l.group_id);
  const listsByGroup = groups.map(g => ({
    ...g,
    lists: lists.filter(l => l.group_id === g.id)
  }));

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

  return (
    <TasksContext.Provider value={{ refreshData, lists, stats }}>
      <div className="flex h-[calc(100vh-64px)] bg-gray-50 dark:bg-gray-900">
        {/* Sidebar */}
        <aside className={cn(
          "bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col",
          sidebarCollapsed ? "w-16" : "w-72"
        )}>
          {/* Search */}
          {!sidebarCollapsed && (
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery) {
                      navigate(`/tasks/search?q=${encodeURIComponent(searchQuery)}`);
                    }
                  }}
                  className="pl-9 h-9"
                />
              </div>
            </div>
          )}

          <ScrollArea className="flex-1">
            <div className="p-2">
              {/* Smart Lists */}
              {smartLists.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                      isActive
                        ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    )}
                    data-testid={`smart-list-${item.id}`}
                  >
                    <Icon className="h-5 w-5" style={{ color: item.color }} />
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.name}</span>
                        {item.count > 0 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{item.count}</span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}

              {!sidebarCollapsed && (
                <>
                  <div className="h-px bg-gray-200 dark:bg-gray-700 my-3" />

                  {/* Lists without group */}
                  {listsWithoutGroup.map((list) => (
                    <ListItem
                      key={list.id}
                      list={list}
                      isActive={location.pathname === `/tasks/list/${list.id}`}
                      onClick={() => navigate(`/tasks/list/${list.id}`)}
                      onDelete={() => handleDeleteList(list.id)}
                    />
                  ))}

                  {/* Grouped Lists */}
                  {listsByGroup.map((group) => (
                    <div key={group.id} className="mt-2">
                      <button
                        onClick={() => toggleGroup(group.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700"
                      >
                        {expandedGroups[group.id] !== false ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                        {group.name}
                      </button>
                      {expandedGroups[group.id] !== false && group.lists.map((list) => (
                        <ListItem
                          key={list.id}
                          list={list}
                          isActive={location.pathname === `/tasks/list/${list.id}`}
                          onClick={() => navigate(`/tasks/list/${list.id}`)}
                          onDelete={() => handleDeleteList(list.id)}
                          className="ml-4"
                        />
                      ))}
                    </div>
                  ))}

                  {/* Add List Button */}
                  <button
                    onClick={() => setShowNewListDialog(true)}
                    className="w-full flex items-center gap-3 px-3 py-2 mt-2 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    data-testid="add-list-btn"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Nouvelle liste</span>
                  </button>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Bottom Actions */}
          {!sidebarCollapsed && (
            <div className="p-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => navigate('/tasks/dashboard')}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm",
                  location.pathname === '/tasks/dashboard'
                    ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                )}
              >
                <BarChart3 className="h-5 w-5" />
                <span>Tableau de bord</span>
              </button>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>

        {/* New List Dialog */}
        <Dialog open={showNewListDialog} onOpenChange={setShowNewListDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle liste</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                placeholder="Nom de la liste"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                autoFocus
              />
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Couleur
                </label>
                <div className="flex gap-2">
                  {colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewListColor(color)}
                      className={cn(
                        "w-8 h-8 rounded-full transition-transform",
                        newListColor === color && "ring-2 ring-offset-2 ring-blue-500 scale-110"
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewListDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateList}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TasksContext.Provider>
  );
}

function ListItem({ list, isActive, onClick, onDelete, className }) {
  return (
    <div className={cn("group flex items-center", className)}>
      <button
        onClick={onClick}
        className={cn(
          "flex-1 flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
          isActive
            ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        )}
        data-testid={`list-item-${list.id}`}
      >
        <div
          className="w-3 h-3 rounded-sm"
          style={{ backgroundColor: list.color || '#6B7280' }}
        />
        <span className="flex-1 text-left truncate">{list.name}</span>
        {list.task_count > 0 && (
          <span className="text-xs text-gray-500">{list.task_count}</span>
        )}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
            <MoreHorizontal className="h-4 w-4 text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onDelete} className="text-red-600">
            Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
